import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import {
  appMutators,
  appQueries,
  type ConnectionConfig,
  type ConnectionInsert,
  type ConnectionUpdate,
} from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

type ZeroStudioConnection = {
  id: string;
  studioID: string;
  name: string;
  connectionType: string;
  host: string;
  port?: number | null;
  model?: string | null;
  managementURL?: string | null;
  enabled: boolean;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

export interface StudioConnection extends ConnectionConfig {
  studio_id: string;
}

function toLegacyConnection(row: ZeroStudioConnection): StudioConnection {
  return {
    id: row.id,
    studio_id: row.studioID,
    name: row.name,
    connection_type: row.connectionType as ConnectionConfig["connection_type"],
    host: row.host,
    port: row.port ?? null,
    model: row.model ?? null,
    management_url: row.managementURL ?? null,
    enabled: row.enabled,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

export const useStudioConnections = (studioId: string | undefined) => {
  const query = studioId
    ? appQueries.studioConnections.byStudio({ studioID: studioId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!studioId });
  const connections = ((data ?? []) as ZeroStudioConnection[]).map(
    toLegacyConnection,
  );

  return {
    data: connections,
    isLoading:
      !!studioId && details.type !== "complete" && connections.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateStudioConnection = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (params: { studio_id: string; connection: ConnectionInsert }) =>
    runPending(async () => {
      const now = new Date().toISOString();
      const input = {
        id: generateUUID(),
        studioID: params.studio_id,
        name: params.connection.name,
        connectionType: params.connection.connection_type,
        host: params.connection.host,
        port: params.connection.port ?? null,
        model: params.connection.model ?? null,
        managementURL: params.connection.management_url ?? null,
        enabled: params.connection.enabled,
      };
      const mutation = zero.mutate(appMutators.studioConnections.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio connection create was rejected",
      );
      return toLegacyConnection({
        ...input,
        createdAt: now,
        updatedAt: now,
      });
    });

  return {
    mutate: (
      params: { studio_id: string; connection: ConnectionInsert },
      options?: MutationOptions<StudioConnection>,
    ) => {
      void run(params)
        .then((data) => {
          toast.success("Device created");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to create device: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};

export const useUpdateStudioConnection = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (params: {
    id: string;
    studio_id: string;
    connection: ConnectionUpdate;
  }) =>
    runPending(async () => {
      const input = {
        id: params.id,
        studioID: params.studio_id,
        ...(params.connection.name !== undefined
          ? { name: params.connection.name }
          : {}),
        ...(params.connection.connection_type !== undefined
          ? { connectionType: params.connection.connection_type }
          : {}),
        ...(params.connection.host !== undefined
          ? { host: params.connection.host }
          : {}),
        ...(params.connection.port !== undefined
          ? { port: params.connection.port ?? null }
          : {}),
        ...(params.connection.model !== undefined
          ? { model: params.connection.model ?? null }
          : {}),
        ...(params.connection.management_url !== undefined
          ? { managementURL: params.connection.management_url ?? null }
          : {}),
        ...(params.connection.enabled !== undefined
          ? { enabled: params.connection.enabled }
          : {}),
      };
      const mutation = zero.mutate(appMutators.studioConnections.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio connection update was rejected",
      );
      return input;
    });

  return {
    mutate: (
      params: {
        id: string;
        studio_id: string;
        connection: ConnectionUpdate;
      },
      options?: { onSuccess?: () => void },
    ) => {
      void run(params)
        .then(() => {
          toast.success("Device updated");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to update device: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};

export const useDeleteStudioConnection = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (params: { id: string; studio_id: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.studioConnections.delete({
          id: params.id,
          studioID: params.studio_id,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio connection delete was rejected",
      );
    });

  return {
    mutate: (
      params: { id: string; studio_id: string },
      options?: { onSuccess?: () => void },
    ) => {
      void run(params)
        .then(() => {
          toast.success("Device deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete device: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};
